<template>
  <div class="game-screen">
    <div v-if="mode === 'dailySuite'" class="side-and-board">
      <previous-boards :boards="previousBoards" />
      <div class="board-block">
        <h2 class="game-title">
          Mot {{ currentSuiteIndex + 1 }}
          <span class="subtitle">{{ solution.length }} lettres</span>
        </h2>
        <board-grid
          :solution="solution"
          :guesses="guesses"
          :current-guess="currentGuess"
          :max-rows="maxRows"
        />
      </div>
    </div>

    <div v-else class="board-block single">
      <board-grid
        :solution="solution"
        :guesses="guesses"
        :current-guess="currentGuess"
        :max-rows="maxRows"
      />
    </div>

    <keyboard
      :solution="solution"
      :keyboard-state="keyboardState"
      :disabled="finished"
      @key="onVirtualKey"
    />

    <end-screen
      v-if="finished"
      :win="win"
      :solution="solution"
      @share="onShare"
    />
  </div>
</template>

<script>
import { evaluateGuess, updateKeyboardState } from "../utils/gameLogic";
import {
  getDailyWord,
  getDailySuiteWords,
  getRandomWord,
  getCurrentDayIndex,
} from "../utils/dailyWords";
import { markSolved } from "../utils/storage";
import BoardGrid from "./BoardGrid.vue";
import Keyboard from "./Keyboard.vue";
import PreviousBoards from "./PreviousBoards.vue";
import EndScreen from "./EndScreen.vue";
import TusmoLogo from "./TusmoLogo.vue";

export default {
  name: "GameView",
  components: {
    BoardGrid,
    Keyboard,
    PreviousBoards,
    EndScreen,
    TusmoLogo,
  },
  props: {
    mode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      solution: "",
      guesses: [],
      currentGuess: "",
      keyboardState: {},
      maxRows: 6,
      finished: false,
      win: false,
      suiteWords: [],
      currentSuiteIndex: 0,
      previousBoards: [],
    };
  },
  computed: {
    modeKey() {
      if (this.mode === "dailyWord") return "dailyWord";
      if (this.mode === "dailySuite") return "dailySuite";
      return "infinite";
    },
  },
  created() {
    this.initGame();
    window.addEventListener("keydown", this.onKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeyDown);
  },
  methods: {
    initGame() {
      if (this.mode === "dailyWord") {
        this.solution = getDailyWord();
      } else if (this.mode === "dailySuite") {
        this.suiteWords = getDailySuiteWords();
        this.currentSuiteIndex = 0;
        this.solution = this.suiteWords[0];
      } else {
        this.solution = getRandomWord();
      }
      this.solution = this.solution.toUpperCase();

      this.guesses = [];
      this.currentGuess = this.solution[0]; // première lettre donnée
      this.keyboardState = {
        [this.solution[0]]: "given",
      };
      this.finished = false;
      this.win = false;
    },
    startNextSuiteWord() {
      const boardSnapshot = {
        solution: this.solution,
        guesses: this.guesses,
      };
      this.previousBoards.push(boardSnapshot);

      this.currentSuiteIndex += 1;
      if (this.currentSuiteIndex >= this.suiteWords.length) {
        this.finishGame(true);
        return;
      }
      this.solution = this.suiteWords[this.currentSuiteIndex].toUpperCase();
      this.guesses = [];
      this.currentGuess = this.solution[0];
      this.keyboardState = {
        [this.solution[0]]: "given",
      };
      this.finished = false;
      this.win = false;
    },
    onKeyDown(e) {
      if (this.finished) return;
      const key = e.key;

      if (key === "Enter") {
        this.submitGuess();
      } else if (key === "Backspace") {
        this.removeChar();
      } else {
        const letter = key.toUpperCase();
        if (/^[A-ZÀÂÄÇÉÈÊËÎÏÔÖÙÛÜŸ]$/.test(letter)) {
          this.addChar(letter);
        }
      }
    },
    onVirtualKey(k) {
      if (k === "ENTER") {
        this.submitGuess();
      } else if (k === "BACK") {
        this.removeChar();
      } else {
        this.addChar(k);
      }
    },
    addChar(ch) {
      if (this.currentGuess.length >= this.solution.length) return;
      if (this.currentGuess.length === 0) {
        this.currentGuess = this.solution[0];
      } else {
        this.currentGuess += ch;
      }
    },
    removeChar() {
      if (this.currentGuess.length <= 1) return; // ne pas supprimer la lettre donnée
      this.currentGuess = this.currentGuess.slice(0, -1);
    },
    submitGuess() {
      if (this.currentGuess.length !== this.solution.length) return;

      const guess = this.currentGuess.toUpperCase();
      const statuses = evaluateGuess(this.solution, guess);
      const newKeyboard = updateKeyboardState(
        this.keyboardState,
        guess,
        statuses
      );

      this.guesses.push({ word: guess, statuses });
      this.keyboardState = newKeyboard;
      this.currentGuess = this.solution[0];

      if (guess === this.solution) {
        if (this.mode === "dailySuite") {
          if (this.currentSuiteIndex === this.suiteWords.length - 1) {
            this.finishGame(true);
          } else {
            this.startNextSuiteWord();
          }
        } else {
          this.finishGame(true);
        }
      } else if (this.guesses.length >= this.maxRows) {
        this.finishGame(false);
      }
    },
    finishGame(win) {
      this.win = win;
      this.finished = true;

      if ((this.mode === "dailyWord" || this.mode === "dailySuite") && win) {
        const day = getCurrentDayIndex();
        markSolved(this.modeKey, day);
      }

      if (this.mode === "infinite" && win) {
        setTimeout(() => {
          this.initGame();
        }, 1200);
      }
    },
    onShare() {
      alert("Partage à implémenter");
    },
  },
};
</script>

<style scoped>
.game-screen {
  /* min-height: 100vh; */
  color: var(--text-main);
  display: flex;
  flex-direction: column;
  align-items: center;
  /* padding: 16px 8px 32px; */
  box-sizing: border-box;
}

.board-block {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.board-block.single {
  width: 100%;
}

.game-title {
  text-align: center;
  margin: 0 0 12px;
  font-size: 28px;
}

.subtitle {
  font-size: 16px;
  margin-left: 8px;
  opacity: 0.85;
}

.side-and-board {
  width: 100%;
  max-width: 960px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 18px;
}

@media (max-width: 720px) {
  .side-and-board {
    flex-direction: column;
    align-items: center;
  }

  .game-title {
    font-size: 22px;
  }
}
</style>
